import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/auth';
import { useNetworkStore } from '../stores/network';

/**
 * Configure axios instance with base URL and interceptors.
 * In dev we use relative /api/v1 so Vite proxies to the backend (no CORS).
 * In production we use full VITE_API_URL + /api/v1.
 */
const API_VERSION = import.meta.env.VITE_API_VERSION || 'v1';
const API_BASE = import.meta.env.DEV
  ? `/api/${API_VERSION}` // Vite proxy forwards /api/* to backend
  : `${(import.meta.env.VITE_API_URL || '').replace(/\/$/, '')}/api/${API_VERSION}`;

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let sessionExpiredRedirectScheduled = false;

function loginPageHref(): string {
  const base = import.meta.env.BASE_URL || '/';
  if (base === '/') return '/login';
  return `${base.replace(/\/$/, '')}/login`;
}

function extractResponseErrorMessage(data: unknown): string | undefined {
  if (data && typeof data === 'object' && data !== null) {
    const obj = data as { error?: string; message?: string };
    return obj.error ?? obj.message;
  }
  if (typeof data === 'string' && data.trim()) {
    return data.trim();
  }
  return undefined;
}

/** Map backend auth errors to messages suitable for login/register forms. */
function formatCredentialErrorMessage(serverMsg: string | undefined, status: number): string {
  const raw = serverMsg?.trim();
  if (raw) {
    const friendly: Record<string, string> = {
      'Invalid credentials': 'Incorrect email or password. Please try again.',
      'MFA token required': 'Enter your six-digit authentication code.',
      'Invalid MFA token': 'That authentication code is incorrect. Please try again.',
      'Account is inactive': 'Your account is inactive. Please contact your administrator.',
    };
    return friendly[raw] ?? raw;
  }
  if (status === 401) {
    return 'Incorrect email or password. Please try again.';
  }
  return 'Sign in failed. Please try again.';
}

/**
 * Request interceptor to add auth token
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const authStore = useAuthStore();
    const token =
      authStore.token ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('thor_token') : null);

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor to handle common errors
 */
apiClient.interceptors.response.use(
  (response) => {
    useNetworkStore().clearBackendUnreachable();
    return response;
  },
  async (error: AxiosError) => {
    const authStore = useAuthStore();
    
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      const reqPath = `${error.config?.baseURL ?? ''}${error.config?.url ?? ''}`;
      const isCredentialRequest =
        reqPath.includes('/auth/login') ||
        reqPath.includes('/auth/register') ||
        (error.config as { skipSessionExpiredRedirect?: boolean } | undefined)?.skipSessionExpiredRedirect;
      if (isCredentialRequest) {
        const serverMsg = extractResponseErrorMessage(error.response?.data);
        const message = formatCredentialErrorMessage(serverMsg, error.response?.status ?? 401);
        return Promise.reject(new Error(message));
      }
      authStore.logout();
      if (!sessionExpiredRedirectScheduled) {
        sessionExpiredRedirectScheduled = true;
        window.location.href = loginPageHref();
      }
      return Promise.reject(new Error('Session expired. Please login again.'));
    }
    
    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      return Promise.reject(new Error('You do not have permission to perform this action.'));
    }
    
    // Handle 404 Not Found
    if (error.response?.status === 404) {
      return Promise.reject(new Error('The requested resource was not found.'));
    }
    
    // 429: do not set backendUnreachable — that shows the full-screen overlay and unmounts
    // the router, which stops execution polling and leaves runs stuck as "running".
    if (error.response?.status === 429) {
      const retryAfter = error.response?.headers?.['retry-after'];
      const message = retryAfter
        ? `Server is rate limiting requests (429). Please try again after ${retryAfter} second${retryAfter === '1' ? '' : 's'}.`
        : 'Too many requests (429). Please wait a moment and try again.';
      const err = new Error(message) as Error & { status?: number };
      err.status = 429;
      return Promise.reject(err);
    }
    
    // Handle 500 Internal Server Error (surface backend message when available)
    if (error.response?.status === 500) {
      const data = error.response?.data;
      let serverMsg: string | undefined;
      if (data && typeof data === 'object' && data !== null) {
        serverMsg = (data as { error?: string; message?: string }).error ?? (data as { error?: string; message?: string }).message;
      } else if (typeof data === 'string' && data.trim()) {
        serverMsg = data.trim();
      }
      const message = serverMsg && serverMsg.length > 0
        ? serverMsg
        : 'An internal server error occurred. Please try again later.';
      return Promise.reject(new Error(message));
    }
    
    // Handle network errors (backend not running or proxy target unreachable)
    if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
      const message = 'Cannot reach the server. Please try again later.';
      useNetworkStore().setBackendUnreachable(message);
      return Promise.reject(new Error(message));
    }
    
    // Timeouts are often a single slow/large request, not a dead server; do not unmount the app.
    if (error.code === 'ECONNABORTED') {
      const message = 'Request timeout. The server may be unreachable or slow.';
      const err = new Error(message) as Error & { code?: string };
      err.code = 'ECONNABORTED';
      return Promise.reject(err);
    }
    
    const errorMessage =
      extractResponseErrorMessage(error.response?.data) ??
      error.message ??
      'An unexpected error occurred';

    return Promise.reject(new Error(errorMessage));
  }
);

export default apiClient;
